/**
 * AI Assistant State Manager
 * 
 * Manages state for the AI Assistant feature.
 * Follows Single Responsibility Principle - handles only state management.
 */

export class AIAssistantState {
  constructor() {
    // Conversations
    this.conversations = [];
    this.bookmarkedConversations = [];
    this.currentConversationId = null;
    this.conversationsTotal = 0;
    this.bookmarkedTotal = 0;
    
    // Messages
    this.messages = [];
    this.messagesTotal = 0;
    
    // Saved Prompts
    this.savedPrompts = [];
    this.savedPromptsTotal = 0;
    
    // UI State
    this.isLoadingMessages = false;
    this.isLoadingConversations = false;
    this.isSidebarOpen = false;
    
    // Abort controller for cancelling requests
    this.currentAbortController = null;

    // Streaming
    this.isStreaming = false;
    this.inFlightAssistantMessageId = null;

    // Selectable lists. Local only. Never restored from history.
    this.pin = null;
    this.commenting = false;
    this.exhaustedListIds = new Set();
    this.lastTurn = null;
  }

  /**
   * Generate a collision-resistant local-only message ID.
   * @param {string} [prefix]
   */
  static makeLocalId(prefix = 'tmp') {
    const rand = Math.random().toString(36).slice(2, 8);
    return `${prefix}-${Date.now()}-${rand}`;
  }

  // ==========================================
  // CONVERSATIONS
  // ==========================================

  getConversations() {
    return this.conversations;
  }

  setConversations(conversations, total) {
    this.conversations = conversations;
    this.conversationsTotal = total;
  }

  addConversation(conversation) {
    this.conversations = [conversation, ...this.conversations];
    this.conversationsTotal++;
  }

  updateConversation(conversationId, updates) {
    this.conversations = this.conversations.map(c => 
      c._id === conversationId ? { ...c, ...updates } : c
    );
    this.bookmarkedConversations = this.bookmarkedConversations.map(c => 
      c._id === conversationId ? { ...c, ...updates } : c
    );
  }

  removeConversation(conversationId) {
    this.conversations = this.conversations.filter(c => c._id !== conversationId);
    this.bookmarkedConversations = this.bookmarkedConversations.filter(c => c._id !== conversationId);
    this.conversationsTotal = Math.max(0, this.conversationsTotal - 1);
    
    if (this.currentConversationId === conversationId) {
      this.startNewChat();
    }
  }

  getBookmarkedConversations() {
    return this.bookmarkedConversations;
  }

  setBookmarkedConversations(conversations, total) {
    this.bookmarkedConversations = conversations;
    this.bookmarkedTotal = total;
  }

  getCurrentConversationId() {
    return this.currentConversationId;
  }

  setCurrentConversation(conversationId) {
    this.currentConversationId = conversationId;
  }

  getCurrentConversation() {
    return this.conversations.find(c => c._id === this.currentConversationId) ||
           this.bookmarkedConversations.find(c => c._id === this.currentConversationId);
  }

  // ==========================================
  // MESSAGES
  // ==========================================

  getMessages() {
    return this.messages;
  }

  setMessages(messages, total) {
    this.messages = messages;
    this.messagesTotal = total;
  }

  addMessage(message) {
    this.messages = [...this.messages, message];
    this.messagesTotal++;
  }

  addOptimisticMessage(content, displayContent) {
    const optimisticMessage = {
      _id: AIAssistantState.makeLocalId('temp'),
      role: 'user',
      content,
      displayContent: displayContent ?? content,
      createdAt: new Date().toISOString(),
      isOptimistic: true,
    };
    this.addMessage(optimisticMessage);
    return optimisticMessage;
  }

  replaceOptimisticMessage(tempId, realMessage) {
    this.messages = this.messages.map(m => 
      m._id === tempId ? realMessage : m
    );
  }

  updateMessage(messageId, updates) {
    this.messages = this.messages.map(m =>
      m._id === messageId ? { ...m, ...updates } : m
    );
  }

  removeMessage(messageId) {
    this.messages = this.messages.filter(m => m._id !== messageId);
    this.messagesTotal = Math.max(0, this.messagesTotal - 1);
  }

  setStreaming(isStreaming) {
    this.isStreaming = isStreaming;
  }

  getStreaming() {
    return this.isStreaming;
  }

  setInFlightAssistantMessageId(id) {
    this.inFlightAssistantMessageId = id;
  }

  getInFlightAssistantMessageId() {
    return this.inFlightAssistantMessageId;
  }

  getPin() {
    return this.pin;
  }

  setPin(pin) {
    this.pin = pin;
  }

  getCommenting() {
    return this.commenting;
  }

  setCommenting(commenting) {
    this.commenting = commenting;
  }

  getExhaustedListIds() {
    return this.exhaustedListIds;
  }

  setExhaustedListIds(ids) {
    this.exhaustedListIds = ids instanceof Set ? ids : new Set(ids || []);
  }

  getLastTurn() {
    return this.lastTurn;
  }

  setLastTurn(turn) {
    this.lastTurn = turn;
  }

  clearListSelection() {
    this.pin = null;
    this.commenting = false;
    this.exhaustedListIds = new Set();
    this.lastTurn = null;
  }

  clearMessages() {
    this.messages = [];
    this.messagesTotal = 0;
  }

  // ==========================================
  // SAVED PROMPTS
  // ==========================================

  getSavedPrompts() {
    return this.savedPrompts;
  }

  setSavedPrompts(prompts, total) {
    this.savedPrompts = prompts;
    this.savedPromptsTotal = total;
  }

  addSavedPrompt(prompt) {
    this.savedPrompts = [prompt, ...this.savedPrompts];
    this.savedPromptsTotal++;
  }

  updateSavedPrompt(promptId, updates) {
    this.savedPrompts = this.savedPrompts.map(p => 
      p._id === promptId ? { ...p, ...updates } : p
    );
  }

  removeSavedPrompt(promptId) {
    this.savedPrompts = this.savedPrompts.filter(p => p._id !== promptId);
    this.savedPromptsTotal = Math.max(0, this.savedPromptsTotal - 1);
  }

  // ==========================================
  // UI STATE
  // ==========================================

  isMessagesLoading() {
    return this.isLoadingMessages;
  }

  setLoadingMessages(isLoading) {
    this.isLoadingMessages = isLoading;
  }

  isConversationsLoading() {
    return this.isLoadingConversations;
  }

  setLoadingConversations(isLoading) {
    this.isLoadingConversations = isLoading;
  }

  isSidebarVisible() {
    return this.isSidebarOpen;
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  setSidebarOpen(isOpen) {
    this.isSidebarOpen = isOpen;
  }

  // ==========================================
  // REQUEST MANAGEMENT
  // ==========================================

  createAbortController() {
    this.cancelCurrentRequest();
    this.currentAbortController = new AbortController();
    return this.currentAbortController;
  }

  cancelCurrentRequest() {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
  }

  // ==========================================
  // NEW CHAT
  // ==========================================

  startNewChat() {
    this.cancelCurrentRequest();
    this.currentConversationId = null;
    this.messages = [];
    this.messagesTotal = 0;
    this.isStreaming = false;
    this.inFlightAssistantMessageId = null;
    this.clearListSelection();
  }
}
